import React, { useState, useEffect } from 'react';
import { userAPI } from '../api';
import { 
    Trophy, ChevronRight, ChevronLeft, Users, 
    Edit2, Save, Award, Plus, Trash2, X
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { useVirtualNumpad as useVirtualNumpadHook, VirtualNumpadToggle, ScoreInputWithNumpad } from './VirtualNumpad';

const UserTournament = ({ token, onError, onSuccess }) => {
    // Virtual numpad preference
    const { useVirtualNumpad, toggleVirtualNumpad } = useVirtualNumpadHook();
    
    // Tab state: 'participate' | 'manage'
    const [activeTab, setActiveTab] = useState('participate');
    
    // ===== PARTICIPATE VIEW STATE =====
    const [participateView, setParticipateView] = useState('list');
    const [participateTournaments, setParticipateTournaments] = useState([]);
    const [participateCurrentTournament, setParticipateCurrentTournament] = useState(null);
    const [participateRounds, setParticipateRounds] = useState([]);
    const [participateCurrentRound, setParticipateCurrentRound] = useState(null);
    const [participateGroups, setParticipateGroups] = useState([]);
    
    // ===== MANAGE VIEW STATE =====
    const [manageView, setManageView] = useState('list');
    const [myTournaments, setMyTournaments] = useState([]);
    const [currentTournament, setCurrentTournament] = useState(null);
    const [rounds, setRounds] = useState([]);
    const [currentRound, setCurrentRound] = useState(null);
    const [groups, setGroups] = useState([]);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [allUsers, setAllUsers] = useState([]);
    
    // Common
    const [loading, setLoading] = useState(false);
    
    // Modal states
    const [showEditScores, setShowEditScores] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [editingGameNumber, setEditingGameNumber] = useState(1);
    const [scoreInputs, setScoreInputs] = useState({});
    
    // Manage modals
    const [showCreateTournament, setShowCreateTournament] = useState(false);
    const [showCreateRound, setShowCreateRound] = useState(false);
    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [showAddMember, setShowAddMember] = useState(false);
    const [showEditGroup, setShowEditGroup] = useState(false);
    const [editingGroupData, setEditingGroupData] = useState(null);
    
    // Form data
    const [tournamentName, setTournamentName] = useState('');
    const [roundName, setRoundName] = useState('');
    const [groupName, setGroupName] = useState('');
    const [numGames, setNumGames] = useState('3');
    const [selectedUserId, setSelectedUserId] = useState('');
    const [editGroupName, setEditGroupName] = useState('');
    const [editNumGames, setEditNumGames] = useState('3');

    useEffect(() => {
        if (activeTab === 'participate') {
            loadParticipateTournaments();
        } else {
            loadMyTournaments();
            loadAllUsers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // ===== PARTICIPATE FUNCTIONS =====
    const loadParticipateTournaments = async () => {
        setLoading(true);
        try {
            const data = await userAPI.getTournaments(token);
            setParticipateTournaments(data);
        } catch (err) {
            onError('Gagal memuat daftar tournament');
        } finally {
            setLoading(false);
        }
    };

    const loadParticipateRounds = async (tournamentId) => {
        setLoading(true);
        try {
            const data = await userAPI.getTournamentRounds(token, tournamentId);
            setParticipateCurrentTournament(data.tournament);
            setParticipateRounds(data.rounds);
            setParticipateView('tournament');
        } catch (err) {
            onError('Gagal memuat babak');
        } finally {
            setLoading(false);
        }
    };

    const loadParticipateGroups = async (roundId, round) => {
        setLoading(true);
        try {
            const data = await userAPI.getTournamentGroups(token, roundId);
            setParticipateCurrentRound(round || data.round);
            setParticipateGroups(data.groups);
            setParticipateView('round');
        } catch (err) {
            onError('Gagal memuat grup');
        } finally {
            setLoading(false);
        }
    };

    // ===== MANAGE FUNCTIONS =====
    const loadMyTournaments = async () => {
        setLoading(true);
        try {
            const data = await userAPI.getMyTournaments(token);
            setMyTournaments(data);
        } catch (err) {
            onError('Gagal memuat tournament');
        } finally {
            setLoading(false);
        }
    };

    const loadAllUsers = async () => {
        try {
            const data = await userAPI.getAllUsers(token);
            setAllUsers(data);
        } catch (err) {
            console.error('Failed to load users');
        }
    };

    const handleCreateTournament = async () => {
        if (!tournamentName.trim()) return;
        try {
            await userAPI.createTournament(token, tournamentName);
            onSuccess('Tournament berhasil dibuat');
            setShowCreateTournament(false);
            setTournamentName('');
            loadMyTournaments();
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal membuat tournament');
        }
    };

    const handleDeleteTournament = async (id) => {
        if (!confirm('Hapus tournament ini beserta semua data?')) return;
        try {
            await userAPI.deleteTournament(token, id);
            onSuccess('Tournament dihapus');
            loadMyTournaments();
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal menghapus tournament');
        }
    };

    const loadManageRounds = async (tournamentId) => {
        setLoading(true);
        try {
            const data = await userAPI.getMyTournamentRounds(token, tournamentId);
            setCurrentTournament(data.tournament);
            setRounds(data.rounds);
            setManageView('tournament');
        } catch (err) {
            onError('Gagal memuat babak');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateRound = async () => {
        if (!roundName.trim()) return;
        try {
            await userAPI.createTournamentRound(token, currentTournament.id, roundName);
            onSuccess('Babak berhasil dibuat');
            setShowCreateRound(false);
            setRoundName('');
            loadManageRounds(currentTournament.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal membuat babak');
        }
    };

    const handleDeleteRound = async (roundId) => {
        if (!confirm('Hapus babak ini beserta semua grup?')) return;
        try {
            await userAPI.deleteTournamentRound(token, currentTournament.id, roundId);
            onSuccess('Babak dihapus');
            loadManageRounds(currentTournament.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal menghapus babak');
        }
    };

    const loadManageGroups = async (roundId) => {
        setLoading(true);
        try {
            const data = await userAPI.getMyTournamentGroups(token, roundId);
            setCurrentRound(data.round);
            setGroups(data.groups);
            setManageView('round');
        } catch (err) {
            onError('Gagal memuat grup');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateGroup = async () => {
        if (!groupName.trim()) return;
        const games = parseInt(numGames);
        if (!games || games < 1) {
            onError('Jumlah game minimal 1');
            return;
        }
        try {
            await userAPI.createTournamentGroup(token, currentRound.id, groupName, games);
            onSuccess('Grup berhasil dibuat');
            setShowCreateGroup(false);
            setGroupName('');
            setNumGames('3');
            loadManageGroups(currentRound.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal membuat grup');
        }
    };

    const handleDeleteGroup = async (groupId) => {
        if (!confirm('Hapus grup ini?')) return;
        try {
            await userAPI.deleteTournamentGroup(token, groupId);
            onSuccess('Grup dihapus');
            loadManageGroups(currentRound.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal menghapus grup');
        }
    };

    const openEditGroup = (group) => {
        setEditingGroupData(group);
        setEditGroupName(group.name);
        setEditNumGames(String(group.num_games || 3));
        setShowEditGroup(true);
    };

    const handleUpdateGroup = async () => {
        if (!editingGroupData) return;
        const games = parseInt(editNumGames);
        if (!games || games < 1) {
            onError('Jumlah game minimal 1');
            return;
        }
        try {
            await userAPI.updateTournamentGroup(token, editingGroupData.id, editGroupName, games);
            onSuccess('Grup berhasil diupdate');
            setShowEditGroup(false);
            loadManageGroups(currentRound.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal update grup');
        }
    };

    const openAddMember = (group) => {
        setCurrentGroup(group);
        setSelectedUserId('');
        setShowAddMember(true);
    };

    const handleAddMember = async () => {
        if (!selectedUserId) return;
        try {
            await userAPI.addTournamentMember(token, currentGroup.id, selectedUserId);
            onSuccess('Anggota ditambahkan');
            setShowAddMember(false);
            loadManageGroups(currentRound.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal menambah anggota');
        }
    };

    const handleRemoveMember = async (groupId, userId) => {
        if (!confirm('Hapus anggota ini?')) return;
        try {
            await userAPI.removeTournamentMember(token, groupId, userId);
            onSuccess('Anggota dihapus');
            loadManageGroups(currentRound.id);
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal menghapus anggota');
        }
    };

    // ===== SCORE FUNCTIONS =====
    const openEditScores = (group, gameNum) => {
        setEditingGroup(group);
        setEditingGameNumber(gameNum);
        const existingScore = group.scores?.find(s => s.game_number === gameNum);
        const inputs = {};
        group.members_data?.forEach(m => {
            const entry = existingScore?.entries?.find(e => e.user_id === m.id);
            // Jika ada score existing, tampilkan. Jika tidak, kosongkan agar mudah input di mobile
            inputs[m.id] = entry ? entry.score.toString() : '';
        });
        setScoreInputs(inputs);
        setShowEditScores(true);
    };

    // Toggle minus sign for score input (for mobile)
    const toggleMinus = (userId) => {
        const currentVal = scoreInputs[userId] || '';
        if (currentVal.startsWith('-')) {
            setScoreInputs({...scoreInputs, [userId]: currentVal.substring(1)});
        } else if (currentVal === '') {
            setScoreInputs({...scoreInputs, [userId]: '-'});
        } else {
            setScoreInputs({...scoreInputs, [userId]: '-' + currentVal});
        }
    };

    // Calculate total score from inputs (rounded to 1 decimal to fix floating point)
    const calculateTotalScore = () => {
        const total = Object.values(scoreInputs).reduce((sum, val) => {
            const num = parseFloat(val);
            return sum + (isNaN(num) ? 0 : num);
        }, 0);
        // Round to 1 decimal place to fix floating point precision
        return Math.round(total * 10) / 10;
    };

    // Validate score input - max 1 decimal place (supports comma as decimal separator)
    const isValidScoreInput = (val) => {
        if (val === '' || val === '-') return true;
        // Normalize comma to dot for validation
        const normalized = val.replace(',', '.');
        // Allow: optional minus, digits, optional dot with max 1 digit after
        return /^-?\d*\.?\d?$/.test(normalized);
    };

    // Handle score input change - normalize comma to dot
    const handleScoreInputChange = (userId, val) => {
        // Replace comma with dot for decimal separator
        const normalizedVal = val.replace(',', '.');
        if (isValidScoreInput(normalizedVal)) {
            setScoreInputs({...scoreInputs, [userId]: normalizedVal});
        }
    };

    const handleSaveScores = async () => {
        if (!editingGroup) return;
        
        // Validate total = 0
        const total = calculateTotalScore();
        if (total !== 0) {
            onError(`Total score harus = 0. Saat ini: ${total > 0 ? '+' : ''}${total}`);
            return;
        }
        
        try {
            // Round scores to 1 decimal before saving
            const entries = Object.entries(scoreInputs).map(([userId, score]) => ({
                user_id: userId,
                score: Math.round((parseFloat(score) || 0) * 10) / 10
            }));
            await userAPI.addTournamentScore(token, editingGroup.id, editingGameNumber, entries);
            onSuccess(`Score Game ${editingGameNumber} tersimpan`);
            setShowEditScores(false);
            if (activeTab === 'participate') {
                loadParticipateGroups(participateCurrentRound.id, participateCurrentRound);
            } else {
                loadManageGroups(currentRound.id);
            }
        } catch (err) {
            onError(err.response?.data?.detail || 'Gagal menyimpan score');
        }
    };

    const getScore = (group, userId, gameNum) => {
        const gameScore = group.scores?.find(s => s.game_number === gameNum);
        const entry = gameScore?.entries?.find(e => e.user_id === userId);
        return entry?.score;
    };

    // ===== NAVIGATION =====
    const goBackParticipate = () => {
        if (participateView === 'round') {
            loadParticipateRounds(participateCurrentTournament.id);
        } else if (participateView === 'tournament') {
            setParticipateView('list');
            loadParticipateTournaments();
        }
    };

    const goBackManage = () => {
        if (manageView === 'round') {
            loadManageRounds(currentTournament.id);
        } else if (manageView === 'tournament') {
            setManageView('list');
            loadMyTournaments();
        }
    };

    // ===== RENDER SCOREBOARD =====
    const renderScoreboard = (groupList, isManage = false) => (
        <div className="space-y-6">
            {groupList.map(group => {
                const gameColumns = Array.from({ length: group.num_games || 3 }, (_, i) => i + 1);
                const sortedMembers = [...(group.members_data || [])].sort(
                    (a, b) => (group.totals?.[b.id] || 0) - (group.totals?.[a.id] || 0)
                );

                return (
                    <div key={group.id} className="stat-card overflow-hidden">
                        <div className="bg-yellow-500/20 px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
                            <div>
                                <h3 className="text-lg font-bold text-yellow-500">Grup {group.name}</h3>
                                <p className="text-xs text-zinc-400">
                                    {group.members_data?.length || 0} pemain • {group.num_games || 3} game
                                </p>
                            </div>
                            {isManage && (
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" onClick={() => openEditGroup(group)} className="border-zinc-700" title="Edit Grup">
                                        <Edit2 className="w-4 h-4" />
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => openAddMember(group)} className="border-zinc-700">
                                        <Plus className="w-4 h-4 mr-1" /> Anggota
                                    </Button>
                                    <Button variant="destructive" size="sm" onClick={() => handleDeleteGroup(group.id)}>
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {sortedMembers.length === 0 ? (
                            <div className="p-4 text-center text-zinc-500">Belum ada pemain di grup ini</div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full">
                                    <thead>
                                        <tr className="border-b border-zinc-700 bg-zinc-800/50">
                                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">#</th>
                                            <th className="text-left py-3 px-4 text-zinc-400 font-medium">Pemain</th>
                                            {gameColumns.map(num => (
                                                <th key={num} className="text-center py-2 px-3 text-zinc-400 font-medium">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <span className="text-xs">Game {num}</span>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => openEditScores(group, num)}
                                                            className="text-xs text-neon-green h-5 px-2"
                                                        >
                                                            <Edit2 className="w-3 h-3" />
                                                        </Button>
                                                    </div>
                                                </th>
                                            ))}
                                            <th className="text-center py-3 px-4 text-yellow-500 font-bold">Total</th>
                                            {isManage && <th className="text-center py-3 px-2"></th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sortedMembers.map((m, idx) => (
                                            <tr key={m.id} className={`border-b border-zinc-800 ${idx === 0 ? 'bg-yellow-500/10' : idx === 1 ? 'bg-zinc-500/10' : idx === 2 ? 'bg-orange-500/10' : ''}`}>
                                                <td className="py-2 px-4 text-center">
                                                    {idx === 0 ? (
                                                        <Trophy className="w-5 h-5 text-yellow-500 mx-auto" />
                                                    ) : idx === 1 ? (
                                                        <span className="text-zinc-300 font-bold">2</span>
                                                    ) : idx === 2 ? (
                                                        <span className="text-orange-400 font-bold">3</span>
                                                    ) : (
                                                        <span className="text-zinc-500">{idx + 1}</span>
                                                    )}
                                                </td>
                                                <td className="py-2 px-4">
                                                    <span className="text-white font-medium text-sm">{m.username}</span>
                                                </td>
                                                {gameColumns.map(num => {
                                                    const rawScore = getScore(group, m.id, num);
                                                    const score = rawScore !== undefined ? Math.round(rawScore * 10) / 10 : undefined;
                                                    return (
                                                        <td key={num} className="text-center py-2 px-3">
                                                            {score !== undefined ? (
                                                                <span className={`text-sm ${score > 0 ? 'text-neon-green' : score < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                                                                    {score > 0 ? '+' : ''}{score}
                                                                </span>
                                                            ) : (
                                                                <span className="text-zinc-600 text-sm">-</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td className="text-center py-2 px-4">
                                                    {(() => {
                                                        const total = Math.round((group.totals?.[m.id] || 0) * 10) / 10;
                                                        return (
                                                            <span className={`font-bold ${total > 0 ? 'text-neon-green' : total < 0 ? 'text-red-400' : 'text-zinc-400'}`}>
                                                                {total > 0 ? '+' : ''}{total}
                                                            </span>
                                                        );
                                                    })()}
                                                </td>
                                                {isManage && (
                                                    <td className="text-center py-2 px-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleRemoveMember(group.id, m.id)}
                                                            className="text-red-400 hover:text-red-300 h-6 px-2"
                                                        >
                                                            <X className="w-3 h-3" />
                                                        </Button>
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );

    // ===== MAIN RENDER =====
    return (
        <div>
            {/* Tabs */}
            <div className="flex gap-2 mb-6">
                <Button
                    variant={activeTab === 'participate' ? 'default' : 'outline'}
                    onClick={() => { setActiveTab('participate'); setParticipateView('list'); }}
                    className={activeTab === 'participate' ? 'bg-neon-green text-void-black' : 'border-zinc-700'}
                >
                    <Trophy className="w-4 h-4 mr-2" />
                    Ikut Tournament
                </Button>
                <Button
                    variant={activeTab === 'manage' ? 'default' : 'outline'}
                    onClick={() => { setActiveTab('manage'); setManageView('list'); }}
                    className={activeTab === 'manage' ? 'bg-neon-green text-void-black' : 'border-zinc-700'}
                >
                    <Award className="w-4 h-4 mr-2" />
                    Kelola Tournament
                </Button>
            </div>

            {/* ===== PARTICIPATE TAB ===== */}
            {activeTab === 'participate' && (
                <>
                    {participateView === 'list' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                        <Trophy className="w-6 h-6 text-yellow-500" />
                                        Tournament Saya
                                    </h2>
                                    <p className="text-sm text-zinc-400">Lihat dan update score tournament yang kamu ikuti</p>
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><span className="spinner" /></div>
                            ) : participateTournaments.length === 0 ? (
                                <div className="empty-state">
                                    <Award className="empty-state-icon mx-auto" />
                                    <p>Kamu belum ikut tournament apapun</p>
                                </div>
                            ) : (
                                <div className="grid gap-4">
                                    {participateTournaments.map(t => (
                                        <div 
                                            key={t.id} 
                                            className="stat-card p-4 flex items-center justify-between cursor-pointer hover:bg-zinc-800/50"
                                            onClick={() => loadParticipateRounds(t.id)}
                                        >
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-white">{t.name}</h3>
                                                <p className="text-sm text-zinc-400">{t.round_count || 0} babak</p>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-neon-green" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {participateView === 'tournament' && (
                        <div>
                            <div className="flex items-center gap-4 mb-6">
                                <Button variant="outline" size="sm" onClick={goBackParticipate} className="border-zinc-700 text-zinc-300 hover:text-white">
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <div className="flex-1">
                                    <h2 className="text-xl font-bold text-white">{participateCurrentTournament?.name}</h2>
                                    <p className="text-sm text-zinc-400">Pilih babak untuk melihat/update score</p>
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><span className="spinner" /></div>
                            ) : participateRounds.length === 0 ? (
                                <div className="empty-state"><Trophy className="empty-state-icon mx-auto" /><p>Belum ada babak</p></div>
                            ) : (
                                <div className="grid gap-4">
                                    {participateRounds.map((r, idx) => (
                                        <div 
                                            key={r.id} 
                                            className="stat-card p-4 flex items-center justify-between cursor-pointer hover:bg-zinc-800/50"
                                            onClick={() => loadParticipateGroups(r.id, r)}
                                        >
                                            <div className="flex-1">
                                                <h3 className="text-lg font-semibold text-white">
                                                    <span className="text-yellow-500 mr-2">#{idx + 1}</span>{r.name}
                                                </h3>
                                                <p className="text-sm text-zinc-400">{r.group_count || 0} grup</p>
                                            </div>
                                            <ChevronRight className="w-5 h-5 text-neon-green" />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {participateView === 'round' && (
                        <div>
                            <div className="flex items-center gap-4 mb-6">
                                <Button variant="outline" size="sm" onClick={goBackParticipate} className="border-zinc-700 text-zinc-300 hover:text-white">
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <div className="flex-1">
                                    <p className="text-sm text-zinc-400">{participateCurrentTournament?.name}</p>
                                    <h2 className="text-xl font-bold text-white">{participateCurrentRound?.name}</h2>
                                </div>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><span className="spinner" /></div>
                            ) : participateGroups.length === 0 ? (
                                <div className="empty-state"><Users className="empty-state-icon mx-auto" /><p>Belum ada grup</p></div>
                            ) : (
                                renderScoreboard(participateGroups, false)
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ===== MANAGE TAB ===== */}
            {activeTab === 'manage' && (
                <>
                    {manageView === 'list' && (
                        <div>
                            <div className="flex items-center justify-between mb-6">
                                <div>
                                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                                        <Award className="w-6 h-6 text-yellow-500" />
                                        Tournament Saya
                                    </h2>
                                    <p className="text-sm text-zinc-400">Buat dan kelola tournament kamu sendiri</p>
                                </div>
                                <Button onClick={() => setShowCreateTournament(true)} className="bg-neon-green text-void-black">
                                    <Plus className="w-4 h-4 mr-2" /> Tournament Baru
                                </Button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><span className="spinner" /></div>
                            ) : myTournaments.length === 0 ? (
                                <div className="empty-state">
                                    <Award className="empty-state-icon mx-auto" />
                                    <p>Belum ada tournament. Buat yang pertama!</p>
                                </div>
                            ) : (
                                <div className="grid gap-4">
                                    {myTournaments.map(t => (
                                        <div key={t.id} className="stat-card p-4 flex items-center justify-between">
                                            <div className="flex-1 cursor-pointer" onClick={() => loadManageRounds(t.id)}>
                                                <h3 className="text-lg font-semibold text-white">{t.name}</h3>
                                                <p className="text-sm text-zinc-400">{t.round_count || 0} babak</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={() => loadManageRounds(t.id)} className="border-zinc-700">
                                                    <ChevronRight className="w-4 h-4" />
                                                </Button>
                                                <Button variant="destructive" size="sm" onClick={() => handleDeleteTournament(t.id)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {manageView === 'tournament' && (
                        <div>
                            <div className="flex items-center gap-4 mb-6">
                                <Button variant="outline" size="sm" onClick={goBackManage} className="border-zinc-700 text-zinc-300 hover:text-white">
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <div className="flex-1">
                                    <h2 className="text-xl font-bold text-white">{currentTournament?.name}</h2>
                                    <p className="text-sm text-zinc-400">Kelola babak tournament</p>
                                </div>
                                <Button onClick={() => setShowCreateRound(true)} className="bg-neon-green text-void-black">
                                    <Plus className="w-4 h-4 mr-2" /> Babak Baru
                                </Button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><span className="spinner" /></div>
                            ) : rounds.length === 0 ? (
                                <div className="empty-state"><Trophy className="empty-state-icon mx-auto" /><p>Belum ada babak</p></div>
                            ) : (
                                <div className="grid gap-4">
                                    {rounds.map((r, idx) => (
                                        <div key={r.id} className="stat-card p-4 flex items-center justify-between">
                                            <div className="flex-1 cursor-pointer" onClick={() => loadManageGroups(r.id)}>
                                                <h3 className="text-lg font-semibold text-white">
                                                    <span className="text-yellow-500 mr-2">#{idx + 1}</span>{r.name}
                                                </h3>
                                                <p className="text-sm text-zinc-400">{r.group_count || 0} grup</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <Button variant="outline" size="sm" onClick={() => loadManageGroups(r.id)} className="border-zinc-700">
                                                    <ChevronRight className="w-4 h-4" />
                                                </Button>
                                                <Button variant="destructive" size="sm" onClick={() => handleDeleteRound(r.id)}>
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {manageView === 'round' && (
                        <div>
                            <div className="flex items-center gap-4 mb-6">
                                <Button variant="outline" size="sm" onClick={goBackManage} className="border-zinc-700 text-zinc-300 hover:text-white">
                                    <ChevronLeft className="w-4 h-4 mr-1" /> Kembali
                                </Button>
                                <div className="flex-1">
                                    <p className="text-sm text-zinc-400">{currentTournament?.name}</p>
                                    <h2 className="text-xl font-bold text-white">{currentRound?.name}</h2>
                                </div>
                                <Button onClick={() => setShowCreateGroup(true)} className="bg-neon-green text-void-black">
                                    <Plus className="w-4 h-4 mr-2" /> Grup Baru
                                </Button>
                            </div>

                            {loading ? (
                                <div className="flex justify-center py-12"><span className="spinner" /></div>
                            ) : groups.length === 0 ? (
                                <div className="empty-state"><Users className="empty-state-icon mx-auto" /><p>Belum ada grup</p></div>
                            ) : (
                                renderScoreboard(groups, true)
                            )}
                        </div>
                    )}
                </>
            )}

            {/* ===== MODALS ===== */}
            {/* Edit Scores Modal */}
            <Dialog open={showEditScores} onOpenChange={setShowEditScores}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Input Score - Grup {editingGroup?.name} - Game {editingGameNumber}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        {/* Virtual Numpad Toggle */}
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-zinc-400">Masukkan score (maks 1 desimal)</p>
                            <VirtualNumpadToggle enabled={useVirtualNumpad} onToggle={toggleVirtualNumpad} />
                        </div>
                        
                        {editingGroup?.members_data?.map(m => (
                            <ScoreInputWithNumpad
                                key={m.id}
                                userId={m.id}
                                username={m.username}
                                value={scoreInputs[m.id] ?? ''}
                                onChange={handleScoreInputChange}
                                useVirtualNumpad={useVirtualNumpad}
                                onToggleMinus={toggleMinus}
                            />
                        ))}
                        
                        {/* Total Score Indicator */}
                        {(() => {
                            const total = calculateTotalScore();
                            const isValid = total === 0;
                            return (
                                <div className={`p-3 rounded-lg border ${isValid ? 'bg-neon-green/10 border-neon-green/30' : 'bg-red-500/10 border-red-500/30'}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm text-zinc-300">Total Score:</span>
                                        <span className={`font-bold text-lg ${isValid ? 'text-neon-green' : 'text-red-400'}`}>
                                            {total > 0 ? '+' : ''}{total}
                                        </span>
                                    </div>
                                    {!isValid && (
                                        <p className="text-xs text-red-400 mt-1">Total harus = 0 untuk menyimpan</p>
                                    )}
                                    {isValid && (
                                        <p className="text-xs text-neon-green mt-1">✓ Total valid</p>
                                    )}
                                </div>
                            );
                        })()}
                        
                        <p className="text-xs text-zinc-500 text-center">Kosongkan jika tidak ikut main, akan dihitung sebagai 0</p>
                        <Button 
                            onClick={handleSaveScores} 
                            className="w-full bg-neon-green text-void-black disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={calculateTotalScore() !== 0}
                        >
                            <Save className="w-4 h-4 mr-2" /> Simpan Score
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create Tournament Modal */}
            <Dialog open={showCreateTournament} onOpenChange={setShowCreateTournament}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle>Buat Tournament Baru</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-white">Nama Tournament</Label>
                            <Input
                                value={tournamentName}
                                onChange={(e) => setTournamentName(e.target.value)}
                                placeholder="Contoh: Tournament Poker 2024"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>
                        <Button onClick={handleCreateTournament} className="w-full bg-neon-green text-void-black">
                            <Plus className="w-4 h-4 mr-2" /> Buat Tournament
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create Round Modal */}
            <Dialog open={showCreateRound} onOpenChange={setShowCreateRound}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle>Buat Babak Baru</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-white">Nama Babak</Label>
                            <Input
                                value={roundName}
                                onChange={(e) => setRoundName(e.target.value)}
                                placeholder="Contoh: Penyisihan, Semifinal"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>
                        <Button onClick={handleCreateRound} className="w-full bg-neon-green text-void-black">
                            <Plus className="w-4 h-4 mr-2" /> Buat Babak
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Create Group Modal */}
            <Dialog open={showCreateGroup} onOpenChange={setShowCreateGroup}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle>Buat Grup Baru</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-white">Nama Grup</Label>
                            <Input
                                value={groupName}
                                onChange={(e) => setGroupName(e.target.value)}
                                placeholder="Contoh: A, B, C"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Jumlah Game</Label>
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={numGames}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d*$/.test(val)) {
                                        setNumGames(val);
                                    }
                                }}
                                placeholder="Contoh: 3"
                                className="bg-zinc-800 border-zinc-700 text-center"
                            />
                            {(numGames === '' || parseInt(numGames) < 1) && (
                                <p className="text-xs text-red-400 mt-1">Jumlah game minimal 1</p>
                            )}
                        </div>
                        {(() => {
                            const isDisabled = !groupName.trim() || !numGames || parseInt(numGames) < 1;
                            return (
                                <Button 
                                    onClick={handleCreateGroup} 
                                    className={`w-full ${isDisabled ? 'bg-zinc-600 cursor-not-allowed' : 'bg-neon-green'} text-void-black`}
                                    disabled={isDisabled}
                                >
                                    <Plus className="w-4 h-4 mr-2" /> Buat Grup
                                </Button>
                            );
                        })()}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Member Modal */}
            <Dialog open={showAddMember} onOpenChange={setShowAddMember}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle>Tambah Anggota ke Grup {currentGroup?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-white">Pilih User</Label>
                            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger className="bg-zinc-800 border-zinc-700">
                                    <SelectValue placeholder="Pilih user..." />
                                </SelectTrigger>
                                <SelectContent className="bg-zinc-800 border-zinc-700">
                                    {allUsers
                                        .filter(u => !currentGroup?.members?.includes(u.id))
                                        .map(u => (
                                            <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                                        ))
                                    }
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={handleAddMember} className="w-full bg-neon-green text-void-black">
                            <Plus className="w-4 h-4 mr-2" /> Tambah Anggota
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Edit Group Modal */}
            <Dialog open={showEditGroup} onOpenChange={setShowEditGroup}>
                <DialogContent className="bg-zinc-900 border-zinc-800">
                    <DialogHeader>
                        <DialogTitle>Edit Grup {editingGroupData?.name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label className="text-white">Nama Grup</Label>
                            <Input
                                value={editGroupName}
                                onChange={(e) => setEditGroupName(e.target.value)}
                                placeholder="Contoh: A, B, C"
                                className="bg-zinc-800 border-zinc-700"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Jumlah Game</Label>
                            <Input
                                type="text"
                                inputMode="numeric"
                                value={editNumGames}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === '' || /^\d*$/.test(val)) {
                                        setEditNumGames(val);
                                    }
                                }}
                                placeholder="Contoh: 3"
                                className="bg-zinc-800 border-zinc-700 text-center"
                            />
                            {(editNumGames === '' || parseInt(editNumGames) < 1) && (
                                <p className="text-xs text-red-400 mt-1">Jumlah game minimal 1</p>
                            )}
                            <p className="text-xs text-zinc-500 mt-1">
                                ⚠️ Jika mengurangi jumlah game, score game yang melebihi akan dihapus
                            </p>
                        </div>
                        {(() => {
                            const isDisabled = !editNumGames || parseInt(editNumGames) < 1;
                            return (
                                <Button 
                                    onClick={handleUpdateGroup} 
                                    className={`w-full ${isDisabled ? 'bg-zinc-600 cursor-not-allowed' : 'bg-neon-green'} text-void-black`}
                                    disabled={isDisabled}
                                >
                                    <Save className="w-4 h-4 mr-2" /> Simpan Perubahan
                                </Button>
                            );
                        })()}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UserTournament;
